import {
  ColumnDef,
  ColumnSizingState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  Row,
  SortingState,
  Table as TableInstance,
  useReactTable,
  ColumnFiltersState,
  VisibilityState,
  TableState,
  TableOptions,
  RowData,
  CoreOptions,
  FilterFn,
} from '@tanstack/react-table';
import type { FlexProps } from 'antd';
import styled from 'styled-components';
import { ColumnOrdering } from './columnOrder/ColumnOrdering';
import { useColumnOrder } from './columnOrder/useColumnOrder';
import { sortingFns } from './columnSorters/sortingFns';
import { DefaultRenderer, VirtualRenderer } from './renderers';
import './types/meta';
import {
  ForwardedRef,
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { WithFilterOptionsProps } from './renderers/WithFilterOptions';
import { TableStore } from './TableContext';
import { renderDefaultCell, useGetIdFromName } from '../form';
import { isObject } from '../utils';
import { AdjustStickyColumns } from './AdjustStickyColumns';
import './columnFilters/filterFnsPatch';
import { filterFns } from './columnFilters/filterFns';

export const MIN_COLUMN_WIDTH = 50;

export interface TableBaseProps<RowData> extends PropsWithChildren {
  id?: string;
  columns: ColumnDef<RowData, any>[];
  columnVisibility?: VisibilityState;
  columnOrderingDisabled?: boolean;
  columnResizeDisabled?: boolean;
  persistColumnStyle?: boolean;
  defaultSort?: SortingState;
  filters?: ColumnFiltersState;
  sort?: SortingState;
  onFilter?: OnChangeFn<ColumnFiltersState>;
  onSort?: OnChangeFn<SortingState>;
  onPagination?: OnChangeFn<PaginationState>;
  rowHtmlAttributes?: (
    row: RowData,
    rowIndex: number,
  ) => HTMLAttributes<HTMLTableRowElement> | undefined;
  pagination?: PaginationState;
  manualFiltering?: boolean;
  manualSorting?: boolean;
  actions?: ReactNode | ((table: TableInstance<any>) => ReactNode);
  actionsInFront?: boolean;
  actionsWrapperProps?: FlexProps;
  disableHeader?: boolean;
  hideHeader?: boolean;
  isLoading?: boolean;
  className?: string;
  globalFilter?: any;
  globalFilterFn?: FilterFn<RowData>;
  hideEmptyImage?: boolean;
  renderBody?: (rows: Row<RowData>[]) => ReactNode;
}

export type TableStickyAndVirtualProps =
  | {
      sticky: true;
      virtual?: boolean;
      estimateRowSize?: (index: number) => number;
      scrollContainerSelector: string;
      scrollContainer?: never;
    }
  | {
      sticky: true;
      virtual?: boolean;
      estimateRowSize?: (index: number) => number;
      scrollContainerSelector?: never;
      scrollContainer: HTMLElement;
    }
  | {
      sticky?: boolean;
      virtual: true;
      estimateRowSize?: (index: number) => number;
      scrollContainerSelector: string;
      scrollContainer?: never;
    }
  | {
      sticky?: boolean;
      virtual: true;
      estimateRowSize?: (index: number) => number;
      scrollContainerSelector?: never;
      scrollContainer: HTMLElement;
    }
  | {
      sticky?: never | false;
      virtual?: never | false;
      estimateRowSize?: never;
      scrollContainerSelector?: never;
      scrollContainer?: never;
    };

export type TableProps<RowData> = TableBaseProps<RowData> &
  TableStickyAndVirtualProps &
  WithFilterOptionsProps & {
    data: RowData[];
    rowKey?: string;
    name: string;
    hideClearFilters?: boolean;
  };

export const Table = forwardRef(
  <RowData,>(
    props: TableProps<RowData>,
    ref: ForwardedRef<TableInstance<RowData>>,
  ) => {
    const getIdFromName = useGetIdFromName();
    const {
      data,
      columns,
      columnVisibility,
      columnOrderingDisabled = false,
      columnResizeDisabled = false,
      persistColumnStyle = true,
      defaultSort,
      filters,
      hideClearFilters,
      hideEmptyImage,
      sort,
      onFilter,
      onSort,
      onPagination,
      rowHtmlAttributes,
      options,
      pagination,
      virtual = false,
      estimateRowSize,
      sticky = false,
      scrollContainerSelector,
      rowKey = 'id',
      name,
      id = getIdFromName(name),
      manualFiltering,
      manualSorting,
      actions,
      actionsInFront,
      actionsWrapperProps,
      disableHeader = false,
      hideHeader = false,
      isLoading,
      children,
      className,
      globalFilter,
      globalFilterFn,
      renderBody,
    } = props;

    const [columnOrder, setColumnOrder, defaultColumnOrder] = useColumnOrder(
      columns,
      persistColumnStyle,
    );
    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

    const [internalSorting, setInternalSorting] = useState<SortingState>(
      defaultSort || [],
    );
    const handleSortingChange: OnChangeFn<SortingState> = useCallback(
      (updaterOrValue) => {
        onSort?.(updaterOrValue);
        setInternalSorting(updaterOrValue);
      },
      [onSort],
    );
    const sorting = typeof sort === 'undefined' ? internalSorting : sort;
    const state: Partial<TableState> = {
      sorting,
      pagination,
      columnOrder,
      columnSizing,
    };

    if (filters) {
      state.columnFilters = filters;
    }
    if (columnVisibility) {
      state.columnVisibility = columnVisibility;
    }
    if (globalFilter !== undefined) {
      state.globalFilter = globalFilter;
    }

    const tableProps: TableOptions<any> = {
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      state,
      onColumnOrderChange: setColumnOrder,
      onColumnSizingChange: setColumnSizing,
      onSortingChange: handleSortingChange,
      onPaginationChange: onPagination,
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
      manualFiltering,
      getExpandedRowModel: getExpandedRowModel(),
      getSubRows,
      manualSorting,
      getRowId: (row: any, index) => (rowKey in row ? row[rowKey] : index),
      sortDescFirst: false,
      filterFns,
      sortingFns,
      defaultColumn: {
        cell: renderDefaultCell,
        sortUndefined: 1,
        sortingFn: sortingFns.localeCompare,
        minSize: MIN_COLUMN_WIDTH,
        maxSize: Number.MAX_SAFE_INTEGER,
      },
      filterFromLeafRows: true,
      /**
       * Abusing options object to make and `name` available in CellContext objects.
       * FormTable `getFormTableFieldPath` utility function uses `name` to construct field path.
       */
      name,
    };

    if (globalFilterFn) {
      tableProps.globalFilterFn = globalFilterFn;
    }

    if (onFilter) {
      tableProps.onColumnFiltersChange = onFilter;
    }
    if (manualFiltering) {
      tableProps.onColumnFiltersChange = onFilter;
      tableProps.manualFiltering = true;
    }

    const table = useReactTable(tableProps);

    useImperativeHandle(ref, () => table, [table]);

    /**
     * Establish scroll container.
     * When table is sticky or virtual, scroll container is required.
     */
    const [scrollContainer, setScrollContainer] = useState<HTMLElement>(
      props.scrollContainer!,
    );
    useEffect(() => {
      if (!scrollContainer && scrollContainerSelector) {
        const element = document.querySelector(scrollContainerSelector);
        if (element instanceof HTMLElement) {
          setScrollContainer(element);
        } else {
          throw new Error(
            `Could not find scroll container element by selector "${scrollContainerSelector}" and no element was passed via scrollContainer prop either but scroll container is required if table is either sticky or virtual!`,
          );
        }
      }
    }, [scrollContainer, scrollContainerSelector]);

    const wrapperRef = useRef<HTMLDivElement>(null);

    const element = (
      <TableStore.Provider
        initialData={{
          id,
          table,
          sticky,
          disableHeader,
          hideHeader,
          hideLeftStickyShadow: true,
          hideRightStickyShadow: true,
          columnResizeDisabled,
          wrapperRef,
          persistColumnStyle,
        }}
      >
        {sticky && (
          <AdjustStickyColumns
            tableName={name}
            scrollContainer={scrollContainer}
          />
        )}
        {virtual ? (
          <VirtualRenderer
            hideClearFilters={hideClearFilters}
            hideEmptyImage={hideEmptyImage}
            actions={actions}
            actionsInFront={actionsInFront}
            actionsWrapperProps={actionsWrapperProps}
            options={options}
            scrollContainer={scrollContainer}
            rowHtmlAttributes={rowHtmlAttributes}
            isLoading={isLoading}
            estimateSize={estimateRowSize}
            className={className}
          />
        ) : (
          <DefaultRenderer
            hideClearFilters={hideClearFilters}
            hideEmptyImage={hideEmptyImage}
            actions={actions}
            actionsInFront={actionsInFront}
            actionsWrapperProps={actionsWrapperProps}
            options={options}
            rowHtmlAttributes={rowHtmlAttributes}
            isLoading={isLoading}
            className={className}
            renderBody={renderBody}
          />
        )}
        {children}
      </TableStore.Provider>
    );
    return (
      <Wrapper id={id} ref={wrapperRef}>
        {columnOrderingDisabled ? (
          element
        ) : (
          <ColumnOrdering
            table={table}
            columnOrder={columnOrder}
            defaultColumnOrder={defaultColumnOrder}
            onColumnOrderChange={setColumnOrder}
            wrapperRef={wrapperRef}
            persistColumnStyle={persistColumnStyle}
          >
            {element}
          </ColumnOrdering>
        )}
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  position: relative;
`;

const getSubRows: Required<CoreOptions<any>>['getSubRows'] = (rowData) =>
  isObject(rowData) && 'children' in rowData && Array.isArray(rowData.children)
    ? rowData.children
    : undefined;

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export interface CoreOptions<TData extends RowData> {
    name: string;
  }
}
